/**
 * HTML input types and value formatting — shared by EntityForm and RuntimeFormRenderer.
 * Attribute metadata wins over FormNode.inputType (designer mock hints).
 */

export type DateTimeFieldMode = 'date' | 'datetime' | 'time'

export function getHtmlInputTypeForField(
  columnType: string | undefined,
  format?: string | null,
): string {
  const typeLower = (columnType || '').toLowerCase()
  const fmt = (format || '').toLowerCase()
  if (typeLower.includes('int') || typeLower === 'number' || typeLower.includes('float')) {
    return 'number'
  }
  if (typeLower.includes('bool')) {
    return 'checkbox'
  }
  if (typeLower === 'datetime' || typeLower.includes('datetime')) {
    return fmt === 'time' ? 'time' : 'datetime-local'
  }
  if (typeLower === 'date' || typeLower.includes('date')) {
    if (fmt === 'time') return 'time'
    if (fmt === 'datetime') return 'datetime-local'
    if (fmt === 'date') return 'date'
    return 'date'
  }
  if (typeLower.includes('time') && !typeLower.includes('date')) {
    return 'time'
  }
  return 'text'
}

/** Map attr_type + Designer format → unified DateTimeField mode (null = not a date field). */
export function resolveDateTimeFieldMode(
  attrType?: string | null,
  format?: string | null,
): DateTimeFieldMode | null {
  const html = getHtmlInputTypeForField(attrType ?? undefined, format)
  if (html === 'date') return 'date'
  if (html === 'datetime-local') return 'datetime'
  if (html === 'time') return 'time'
  return null
}

export function isNativeDateInputType(inputType: string): boolean {
  return inputType === 'date' || inputType === 'datetime-local' || inputType === 'time'
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** Parse stored DB/form value into a local Date for the picker. */
export function parseStoredToDate(value: unknown, mode: DateTimeFieldMode): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const s = String(value).trim()
  if (mode === 'time') {
    const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/)
    if (m) {
      const d = new Date()
      d.setHours(Number(m[1]), Number(m[2]), Number(m[3] || 0), 0)
      return d
    }
  }
  if (mode === 'date' && /^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, mo, day] = s.slice(0, 10).split('-').map(Number)
    return new Date(y, mo - 1, day)
  }
  // datetime-local without Z: treat as local wall time
  if (mode === 'datetime' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(s)) {
    const [datePart, timePart] = s.split('T')
    const [y, mo, day] = datePart.split('-').map(Number)
    const [hh, mm, ss] = timePart.split(':').map((x) => Number(x.slice(0, 2)))
    return new Date(y, mo - 1, day, hh || 0, mm || 0, ss || 0)
  }
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Copy Y/M/D from `d`, wall-clock H/M/S from `now`. */
export function stampCurrentTime(d: Date, now: Date = new Date()): Date {
  const next = new Date(d)
  next.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0)
  return next
}

/**
 * Calendar pick on a datetime field stamps "now" as the time.
 * Typing in the closed field keeps whatever time the user entered.
 */
export function applyPickerDate(
  d: Date | null,
  mode: DateTimeFieldMode,
  pickerOpen: boolean,
  now: Date = new Date(),
): Date | null {
  if (!d || Number.isNaN(d.getTime())) return null
  if (mode === 'datetime' && pickerOpen) return stampCurrentTime(d, now)
  return d
}

/** Serialize picker Date back to the storage string shape used by runtime/EntityForm. */
export function formatDateToStored(d: Date | null, mode: DateTimeFieldMode): string | null {
  if (!d || Number.isNaN(d.getTime())) return null
  if (mode === 'date') {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
  }
  if (mode === 'time') {
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

/** HTML date/datetime-local/time need strict string shapes; DB returns ISO strings. */
export function htmlInputValueFromStored(val: unknown, inputType: string): string {
  if (val === null || val === undefined || val === '') return ''
  if (inputType === 'date') {
    const d = parseStoredToDate(val, 'date')
    if (d) return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    const s = String(val)
    return s.length >= 10 ? s.slice(0, 10) : s
  }
  if (inputType === 'datetime-local') {
    const d = parseStoredToDate(val, 'datetime')
    if (d) {
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    }
    const s = String(val).replace('Z', '')
    return s.length >= 16 ? s.slice(0, 16) : s
  }
  if (inputType === 'time') {
    const d = parseStoredToDate(val, 'time')
    if (d) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    const s = String(val)
    const m = s.match(/(\d{1,2}:\d{2})/)
    return m ? m[1].slice(0, 5) : s.slice(0, 5)
  }
  return String(val)
}

const ATTR_DRIVEN_INPUT_TYPES = new Set([
  'string',
  'number',
  'boolean',
  'date',
  'datetime',
  'time',
  'text',
  'ref',
  'enum_ref',
  'composite_ref',
  'uuid',
])

/**
 * Resolve MUI TextField `type` for runtime: metadata attr_type/format first (same as EntityForm),
 * FormNode.inputType only when attr is unknown or plain string without semantic type.
 */
export function resolveRuntimeInputType(
  attrType: string | undefined,
  format?: string | null,
  nodeInputType?: string,
): string {
  const t = (attrType || '').toLowerCase()
  if (t && ATTR_DRIVEN_INPUT_TYPES.has(t)) {
    return getHtmlInputTypeForField(attrType, format)
  }
  if (nodeInputType?.trim()) return nodeInputType.trim()
  return getHtmlInputTypeForField(attrType, format)
}
